<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { beginWait } from '../helpers/pageLoad';

/**
 * Content already on screen being fetched again — the next page of a table,
 * a filter applied. It stays where it is, dimmed a little, and the progress
 * line at the top says something is on its way. Never a spinner in the middle
 * of it (prototype "WAITING, WITHOUT A SPINNER"): nothing here is new content
 * whose shape a skeleton could show, and a centred spinner over rows the eye
 * is still reading is exactly the interruption the pattern removes.
 *
 * Not for the first 200ms, as the line and the skeletons: a quick re-fetch
 * dimmed and undimmed the rows in a blink, which read as a flicker.
 */
defineProps({
  // Kept so existing callers keep working.
  size: { type: String, default: null },
});

const shown = ref(false);
let endWait = null;
let timer = null;
onMounted(() => {
  endWait = beginWait();
  timer = setTimeout(() => { shown.value = true; }, 200);
});
onBeforeUnmount(() => {
  clearTimeout(timer);
  endWait?.();
});
</script>

<template>
  <div class="loading__overlay" :class="{ 'loading__overlay--shown': shown }" aria-busy="true" data-testid="loading-overlay" />
</template>

<style lang="scss" scoped>
.loading__overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(255, 255, 255, 0.45);
  z-index: 50;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.14s ease-out;
}

.loading__overlay--shown { opacity: 1; }

@media (prefers-reduced-motion: reduce) {
  .loading__overlay { transition: none; }
}
</style>
